import { Page, PageStatus, Planogram } from "src/components";
import { MerchflowHeader } from "../../components/merchflows/MerchflowHeader/MerchflowHeader";
import { useParams } from "react-router-dom";
import { MerchflowStepsNavigator } from "../../components/merchflows/MerchflowStepsNavigator/MerchflowStepsNavigator";

import { useAsyncState } from "src/hooks/useAsyncState";
import { useEffect, useMemo } from "react";
import { useApi } from "src/api";
import {
  ResponseGetMerchflows_merchflowId_S2_s2SolutionId_,
  RouteGetMerchflows_merchflowId_S2_s2SolutionId_,
} from "@CommonApi/merchflows/_merchflowId_/s2/_s2SolutionId_";
import { useLoaders } from "src/components/InlineLoader";
import { PrimitiveAtom, useAtom } from "jotai";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { merchflows_merchflowId_S2Solution_s2SolutionId_PogAtom } from "./store/atoms";
import { Button, Flex } from "src/elements";
import { PlanogramHovererDetails } from "src/components/PlanogramExtensions/PlanogramHovererDetails/PlanogramHovererDetails";
import { PlanogramMetrics } from "src/components/PlanogramExtensions/PlanogramMetrics";
import { S2LogsButton } from "./components/S2LogsButton";
import { calculateContainerAlgoPoints } from "@CommonUtils/pog/container";
import { useStorePlanogramRulesModal } from "src/modals/StorePlanogramRules/store/hooks";

export const PageS2Solution = () => {
  const { merchflowId: _merchflowId, s2SolutionId: _s2SolutionId } = useParams<{
    merchflowId: string;
    s2SolutionId: string;
  }>();
  const merchflowId = Number(_merchflowId);
  const s2SolutionId = Number(_s2SolutionId);

  const { getS2SolutionApi } = useApi();
  const { hasLoader } = useLoaders();
  const { openStorePlanogramRules } = useStorePlanogramRulesModal();

  const [data, setData] = useAsyncState<ResponseGetMerchflows_merchflowId_S2_s2SolutionId_>();
  const [pog, setPog] = useAtom(merchflows_merchflowId_S2Solution_s2SolutionId_PogAtom);

  const isLoading =
    data === undefined || hasLoader(RouteGetMerchflows_merchflowId_S2_s2SolutionId_);

  useEffect(() => {
    loadS2Solution();

    return () => {
      setPog(null);
    };
  }, []);

  // Effect to check if min product heights are met
  const isMinProductsHeightMet = useMemo(() => {
    return (
      pog !== null &&
      pog.containers.every(
        (container) => calculateContainerAlgoPoints(container, pog).minProductHeightMet,
      )
    );
  }, [pog]);

  const loadS2Solution = async () => {
    const response = await getS2SolutionApi({ merchflowId, s2SolutionId });

    setData(response);

    if (response) {
      setPog(new Pog(response.solution));
    }
  };

  const generateBasePog = async () => {
    if (pog) {
      openStorePlanogramRules({
        mode: "base",
        merchflowId,
        s2SolutionId,
        pog: pog.toNonCircular(),
      });
    }
  };

  return (
    <Page>
      <MerchflowHeader merchflowId={merchflowId} templateId={data?.template_id} />

      <MerchflowStepsNavigator
        activeStep="S2"
        s1UserTriggerId={data?.s1_user_trigger_id}
        merchflowId={merchflowId}
      />

      {isLoading && <PageStatus label="Loading mudmap solution..." icon="spinner" />}

      {!isLoading && data && pog && (
        <Flex column flexGrow gap="10px">
          <Flex flexGrow>
            <Planogram
              pogAtom={
                merchflows_merchflowId_S2Solution_s2SolutionId_PogAtom as unknown as PrimitiveAtom<Pog>
              }
              isEditable
              merchflowId={merchflowId}
              isWithBorder
              isPanelVisible
              isContainersFullDetails
              isMetricsRecalculated={false}
            />
          </Flex>

          <PlanogramHovererDetails />

          <Flex justify="between" align="center">
            <PlanogramMetrics
              pogAtom={merchflows_merchflowId_S2Solution_s2SolutionId_PogAtom}
              isLoading={isLoading}
            />

            <Flex gap="10px">
              <S2LogsButton merchflowId={merchflowId} s2SolutionId={s2SolutionId} />

              <Button
                isDisabled={!isMinProductsHeightMet}
                color={!isMinProductsHeightMet ? "red" : "primary"}
                isLoading={isLoading}
                onClick={generateBasePog}
              >
                Generate Base Planogram
              </Button>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Page>
  );
};
